import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { packShareHref, type PackShareChatCardData } from '../../../../lib/pack-share-chat-card';
import { Button, Card, Typography } from '../../ui';
import styles from './ShareAskCard.module.css';

/**
 * "Share this pack" — under the reply of the turn that created a pack (`metadata.packShareCard`).
 *
 * It does not re-implement sharing: the button opens the pack's own screen with its existing share
 * flow started (`/packs/<slug>?share=1`, read by both pack screens through `useShareOnArrival`), so the
 * re-enable gate, the validation dialog and the link dialog are the ones the pack screen already has.
 *
 * Named `ChatPackShareCard` to keep it apart from the pack screen's own `packs/PackShareCard`.
 * Shared 1:1 with the phones' pack share cards.
 */
export function ChatPackShareCard({ card }: { card: PackShareChatCardData }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const seenRef = useItemSeenBi('pack-share-card', { additionalFields: { pack_slug: card.slug } });
  const title = t('chat.packShareCard.title');

  return (
    <Card component="section" ref={seenRef} className={styles.card} aria-label={title} data-testid="chat-pack-share-card">
      <Typography variant="h4" component="h3">
        {title}
      </Typography>
      <Typography variant="p2" component="p" color="secondary">
        {t('chat.packShareCard.body', { name: card.name })}
      </Typography>
      <div className={styles.actions}>
        <Button
          fullWidth
          href={packShareHref(card.slug)}
          onClick={() =>
            logPageClickBi(
              { page_name: 'chat', button_name: 'pack_share_card_share' },
              { componentName: 'pack_share_card_share', additionalFields: { pack_slug: card.slug } },
            )
          }
        >
          {t('chat.packShareCard.share')}
        </Button>
      </div>
    </Card>
  );
}
